import React, { type ReactNode, useMemo } from "react"

import { type Role } from "../../containers/LoginPage/types/auth.types.ts"
import type {
  PermissionFeatures,
  PermissionPages,
  UserPermissions
} from "../../containers/SettingsPermissions/types/permissions.types.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { NotAllowed } from "./NotAllowed.tsx"

type PermissionCategories = keyof PermissionFeatures

// category.section.key, e.g. "projects.projects.project_create"
type PermissionPath = {
  [Cat in PermissionCategories]: {
    [Sec in keyof PermissionFeatures[Cat]]: {
      [Key in keyof PermissionFeatures[Cat][Sec]]: `${Cat & string}.${Sec & string}.${Key & string}`
    }[keyof PermissionFeatures[Cat][Sec]]
  }[keyof PermissionFeatures[Cat]]
}[PermissionCategories]

interface PermissionGateProps {
  children: ReactNode
  /** Pages that should be in permissions.pages */
  pages?: PermissionPages[]
  /** Paths to permission (category.section.key) */
  paths?: PermissionPath[]
  /** "every" — all paths must be true, "some" — at least one */
  mode?: "every" | "some"
  /** Custom check. Wrap in useCallback to avoid unnecessary recalculations */
  check?: (permissions: UserPermissions, role: Role) => boolean
  fallback?: ReactNode
  description?: string
  hideOnDenied?: boolean
}

const EMPTY_PATHS: PermissionPath[] = []

const getPermissionByPath = (
  permissions: UserPermissions,
  path: PermissionPath
): boolean => {
  const [category, section, key] = path?.split(".") as [
    PermissionCategories,
    string,
    string
  ]
  const cat = permissions.codes?.[category]
  if (!cat) return false
  const sec = (cat as Record<string, Record<string, boolean | undefined>>)[
    section
  ]
  if (!sec) return false
  return !!sec[key]
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  pages,
  paths,
  mode = "every",
  check,
  fallback,
  description,
  hideOnDenied = false
}) => {
  const permissions = useAppSelector((state) => state.auth.permissions)
  const role = useAppSelector((state) => state.auth.userInfo?.role)

  const resolvedPaths = paths ?? EMPTY_PATHS

  const isAllowed = useMemo(() => {
    if (!permissions) return false

    const hasPageAccess =
      !pages?.length || pages.every((p) => permissions.pages?.includes(p))

    const hasPathsAccess =
      resolvedPaths.length === 0
        ? true
        : mode === "every"
          ? resolvedPaths.every((path) =>
              getPermissionByPath(permissions, path)
            )
          : resolvedPaths.some((path) => getPermissionByPath(permissions, path))

    const passesCheck =
      !check || (role !== undefined && check(permissions, role))

    return hasPageAccess && hasPathsAccess && passesCheck
  }, [permissions, role, pages, resolvedPaths, mode, check])

  if (isAllowed) return <>{children}</>
  if (hideOnDenied) return null

  return <NotAllowed fallback={fallback} description={description} />
}

export type { PermissionPath, PermissionGateProps }
